// src/pages/Tasks/TaskDetail.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { tasksApi, Task, TaskAssignee, TaskComment, TaskAttachment, TaskChecklistItem, TaskActivity } from '../../api/tasks';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import CommentSection from '../../components/CommentSection';
import Checklist from '../../components/Checklist';
import StatusBadge from '../../components/StatusBadge';
import AssigneeSelector from '../../components/AssigneeSelector';
import { useUserInfo } from '../../hooks/useUserInfo';
import { usePermissionGuard } from '../../hooks/usePermissionGuard';
import { useFileDropPaste } from '../../hooks/useFileDropPaste';
import { Popup } from '../../components/Popup';
import { JourneyStrip } from '../../components/JourneyStrip';
import { ApprovalChain } from '../../components/ApprovalChain';
import { MuteTaskButton } from '../../components/MuteTaskButton';
import { displayedProgress } from '../../utils/taskStatus';
import { AttachmentThumb } from '../../components/AttachmentThumb';
import ConfirmDialog, { type ConfirmState } from '../../components/ConfirmDialog';
import { useAuth } from '../../contexts/AuthContext';
import { useAccess } from '../../contexts/AccessContext';

// Add interface for file upload
interface FileWithPreview extends File {
  preview?: string;
  uploadProgress?: number;
}

// Status flow configuration
const statusFlow = {
  'TODO': { next: 'IN_PROGRESS', prev: null, color: '#6B7280', bg: '#F3F4F6', icon: '📝', label: 'To Do', order: 1 },
  'IN_PROGRESS': { next: 'IN_REVIEW', prev: 'TODO', color: '#2563EB', bg: '#EFF6FF', icon: '⚙️', label: 'In Progress', order: 2 },
  'IN_REVIEW': { next: 'DONE', prev: 'IN_PROGRESS', color: '#7C3AED', bg: '#F5F3FF', icon: '🔍', label: 'In Review', order: 3 },
  'BLOCKED': { next: 'IN_PROGRESS', prev: null, color: '#DC2626', bg: '#FEF2F2', icon: '🚫', label: 'Hold', order: 4 },
  'DONE': { next: null, prev: 'IN_REVIEW', color: '#059669', bg: '#ECFDF5', icon: '✅', label: 'Done', order: 5 },
  'CANCELLED': { next: null, prev: null, color: '#6B7280', bg: '#F3F4F6', icon: '❌', label: 'Cancelled', order: 6 },
};

function TaskDetail() {
  // const { id: taskId } = useParams<{ id: string }>();
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const tasksHome =
    user?.subscription?.workspace_mode === 'limited'
      ? '/limited-workspace/tasks'
      : '/tasks/list';
  const { getUserName, getUserRole, currentUser } = useUserInfo();
  const { canManage } = useAccess();
  const canManageTasks = canManage('tasks');
  const { guard, showError, showSuccess, showWarning, popup, closePopup } = usePermissionGuard();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [task, setTask] = useState<Task | null>(null);
  const [assignees, setAssignees] = useState<TaskAssignee[]>([]);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [checklist, setChecklist] = useState<TaskChecklistItem[]>([]);
  const [activity, setActivity] = useState<TaskActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteAttachmentConfirm, setShowDeleteAttachmentConfirm] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<{ id: string; name: string } | null>(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [blockedReason, setBlockedReason] = useState('');
  const [progressPercent, setProgressPercent] = useState(0);
  const [actualHours, setActualHours] = useState('');
  const [progressNote, setProgressNote] = useState('');
  const [searchParams] = useSearchParams();
  const initialTab = ((): 'details' | 'checklist' | 'comments' | 'attachments' | 'activity' => {
    const q = (searchParams.get('tab') || '').toLowerCase();
    const valid = ['details', 'checklist', 'comments', 'attachments', 'activity'] as const;
    return (valid as readonly string[]).includes(q)
      ? (q as 'details' | 'checklist' | 'comments' | 'attachments' | 'activity')
      : 'details';
  })();
  const [activeTab, setActiveTab] = useState<'details' | 'checklist' | 'comments' | 'attachments' | 'activity'>(initialTab);
  
  const [uploadingFiles, setUploadingFiles] = useState<FileWithPreview[]>([]);
  const [uploading, setUploading] = useState(false);
 
  const [rejectedFiles, setRejectedFiles] = useState<{ name: string; size: number }[]>([]);
    const [previewImage, setPreviewImage] = useState<{ src: string; name: string } | null>(null);
  const [deletingAttachment, setDeletingAttachment] = useState<string | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [savingField, setSavingField] = useState<'title' | 'description' | null>(null);
  // In-app confirmation dialog (replaces native window.confirm).
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  // Get available actions based on current status and user role
  const getAvailableActions = () => {
    if (!task) return [];
    const actions: Array<{ label: string; action: string; color: string; requiresConfirm: boolean; confirmMessage?: string; isSendForReview?: boolean }> = [];
    const uid = currentUser?.id;
    const isPrimaryAssignee = task.primary_assignee_id === uid;
    // Acting on a task you're not assigned to = the "tasks" panel at Manage in
    // Access Management. No role list.
    const isAdmin = canManageTasks;
    const isManager = canManageTasks;
    const isPreparor = (assignees ?? []).some(a => a.user_id === uid && a.role_in_task === 'PREPAROR' && a.is_active);
    const isReviewer = (assignees ?? []).some(a => a.user_id === uid && a.role_in_task === 'REVIEWER' && a.is_active);
    const hasReviewer = (assignees ?? []).some(a => a.role_in_task === 'REVIEWER' && a.is_active);
    // Collaborators and (non-primary) owners "work on it, can update" — the backend
    // (can_modify_task) authorizes them to change status, so they must get the
    // "Start Working"/worker actions too. Reviewers stay excluded (review flow).
    const isCollaborator = (assignees ?? []).some(a => a.user_id === uid && a.role_in_task === 'COLLABORATOR' && a.is_active);
    const isOwnerAssignee = (assignees ?? []).some(a => a.user_id === uid && a.role_in_task === 'OWNER' && a.is_active);
    const isWorker = isPrimaryAssignee || isPreparor || isCollaborator || isOwnerAssignee || isAdmin || isManager;

    // TODO status actions
    if (task.status === 'TODO' && isWorker) {
      actions.push({ label: '▶️ Start Working', action: 'IN_PROGRESS', color: 'blue', requiresConfirm: false });
    }

    // IN_PROGRESS status actions
    // "Send for Review" / "Mark Complete" belong to the preparer side only — reviewers must never see them.
    // "Hold Task" is available to both sides (preparer and reviewer) so a reviewer can pause work too.
    if (task.status === 'IN_PROGRESS' && isWorker && !isReviewer) {
      if (hasReviewer) {
        actions.push({ label: '📤 Send for Review', action: 'SEND_FOR_REVIEW', color: 'purple', requiresConfirm: false, isSendForReview: true });
      } else {
        actions.push({ label: '✅ Mark Complete', action: 'DONE', color: 'green', requiresConfirm: false });
      }
    }
    if (task.status === 'IN_PROGRESS' && (isWorker || isReviewer)) {
      actions.push({ label: '🚫 Hold Task', action: 'BLOCKED', color: 'red', requiresConfirm: true, confirmMessage: 'Please provide a reason for putting this task on hold:' });
    }

    // IN_REVIEW status actions — for assigned reviewers and admins/managers
    if (task.status === 'IN_REVIEW' && (isAdmin || isManager || isReviewer)) {
      actions.push({ label: '✅ Approve & Complete', action: 'DONE', color: 'green', requiresConfirm: false });
      actions.push({ label: '🔄 Request Changes', action: 'IN_PROGRESS', color: 'orange', requiresConfirm: false });
    }

    // BLOCKED status actions
    if (task.status === 'BLOCKED' && isWorker) {
      actions.push({ label: '🔓 Unblock & Continue', action: 'IN_PROGRESS', color: 'blue', requiresConfirm: false });
    }

    // Closed task → reopen
    if ((task.status === 'DONE' || task.status === 'CANCELLED') && (isAdmin || isManager || isWorker)) {
      actions.push({ label: '↩️ Reopen Task', action: 'REOPEN', color: 'gray', requiresConfirm: false, isReopen: true } as any);
    }

    return actions;
  };

  // Get status statistics
  const getStatusStats = () => {
    const total = Object.keys(statusFlow).length;
    const currentIndex = statusFlow[task?.status as keyof typeof statusFlow]?.order || 1;
    const completed = ['DONE', 'CANCELLED'].includes(task?.status || '') ? total : currentIndex - 1;
    return { completed, total, progress: (completed / total) * 100 };
  };

  useEffect(() => {
    if (taskId) {
      loadTaskDetail();
    }
  }, [taskId]);

  // Auto-refresh whenever the user comes back to the tab. Fixes the case
  // where a checklist item is marked complete from the Daily Planner in
  // one tab/route and the user expects the TaskDetails checklist to
  // reflect that the next time they look at it — without a manual reload.
  useEffect(() => {
    if (!taskId) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        loadTaskDetail({ silent: true });
      }
    };
    const onFocus = () => loadTaskDetail({ silent: true });
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [taskId]);

  const loadTaskDetail = async (opts?: { silent?: boolean }) => {
    if (!taskId) return;

    if (!opts?.silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const response = await tasksApi.get(taskId);
      setTask(response.data.task);
      setAssignees(response.data.assignees || []);
      setComments(response.data.comments || []);
      setAttachments(response.data.attachments || []);
      setChecklist(response.data.checklist || []);
      setActivity(response.data.activity || []);
      setProgressPercent(response.data?.task?.progress_percent ?? 0);
    } catch (err: any) {
      if (!opts?.silent) {
        setError(err.message || 'Failed to load task details');
      }
    } finally {
      if (!opts?.silent) {
        setLoading(false);
      }
    }
  };

  const applyMutationResponse = (updatedTask: any) => {
    if (updatedTask) {
      setTask((prev) => (prev ? { ...prev, ...updatedTask } : updatedTask));
      if (typeof updatedTask.progress_percent === 'number') {
        setProgressPercent(updatedTask.progress_percent);
      }
    }
    // Fire-and-forget — we already updated the visible task fields above.
    loadTaskDetail({ silent: true });
  };

  // Handle quick status change from action buttons
  const handleQuickStatusChange = async (action: string, requiresConfirm: boolean = false, _confirmMessage?: string) => {
    // BLOCKED uses a styled modal instead of the native browser prompt.
    if (action === 'BLOCKED' && requiresConfirm) {
      setBlockReason('');
      setShowBlockModal(true);
      return;
    }

    await guard(async () => {
      const res = await tasksApi.changeStatus(taskId, {
        status: action,
      });
      showSuccess('Status Updated', `Task status changed to ${statusFlow[action]?.label || action}`);
      applyMutationResponse(res?.data?.task);
    }, 'CANNOT_MODIFY_OTHERS_TASK');
  };

  // Submit the Hold Task modal
  const confirmHoldTask = async () => {
    const reason = blockReason.trim();
    if (!reason) {
      showError('CANNOT_BLOCK_WITHOUT_REASON', 'Please provide a reason for putting this task on hold');
      return;
    }
    await guard(async () => {
      const res = await tasksApi.changeStatus(taskId, {
        status: 'BLOCKED',
        blocked_reason: reason,
      });
      setShowBlockModal(false);
      setBlockReason('');
      showSuccess('Status Updated', 'Task status changed to Hold');
      applyMutationResponse(res?.data?.task);
    }, 'CANNOT_MODIFY_OTHERS_TASK');
  };

  // Handle send-for-review action
  const handleSendForReview = async () => {
    await guard(async () => {
      const res = await tasksApi.sendForReview(taskId!);
      showSuccess('Sent for Review', 'Task has been submitted for review. The reviewer has been notified.');
      applyMutationResponse(res?.data?.task);
    }, 'CANNOT_SEND_FOR_REVIEW');
  };

  // Shared by file-input change, drag-drop, and paste paths.
  const acceptFiles = React.useCallback((files: File[]) => {
    if (!files.length) return;
    const TASK_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
    const tooBig = files.filter(f => f.size > TASK_ATTACHMENT_MAX_BYTES);
    const acceptable = files.filter(f => f.size <= TASK_ATTACHMENT_MAX_BYTES);
    if (tooBig.length > 0) {
      setRejectedFiles(prev => [
        ...prev,
        ...tooBig.map(f => ({ name: f.name, size: f.size })),
      ]);
      showError(
        'File too large',
        `${tooBig.map(f => f.name).join(', ')} exceeds the 5 MB limit and will not be uploaded.`,
      );
    } else {
      setRejectedFiles([]);
    }
    const newFiles: FileWithPreview[] = acceptable.map(file =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        uploadProgress: 0,
      })
    );
    setUploadingFiles(prev => [...prev, ...newFiles]);
  }, [showError]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    acceptFiles(Array.from(e.target.files || []));
    // Reset input so the same (rejected) file can be reselected after fixing.
    if (e.target) e.target.value = '';
  };

  // Drop and Ctrl/Cmd+V paste only while the Attachments tab is open.
  const { dropRef: attachmentDropRef, isDragOver: attachmentDragOver } = useFileDropPaste({
    onFiles: acceptFiles,
    disabled: activeTab !== 'attachments',
    pasteOnDocument: true,
  });

  const dismissRejection = (index: number) => {
    setRejectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (uploadingFiles.length === 0) return;
    if (!taskId) return;

    setUploading(true);
    
    for (const file of uploadingFiles) {
      try {
        file.uploadProgress = 50;
        setUploadingFiles([...uploadingFiles]);
        
        await tasksApi.uploadAttachment(taskId, file);
        
        file.uploadProgress = 100;
        setUploadingFiles([...uploadingFiles]);
        
        showSuccess('File Uploaded', `${file.name} has been uploaded successfully`);
        await loadTaskDetail({ silent: true });
        
        setTimeout(() => {
          setUploadingFiles(prev => prev.filter(f => f.name !== file.name));
        }, 1000);
        
      } catch (err: any) {
        console.error('Upload failed:', err);
        showError('UPLOAD_FAILED', err.message || 'Failed to upload file');
        setUploadingFiles(prev => prev.filter(f => f.name !== file.name));
      }
    }
    
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeQueuedFile = (index: number) => {
    const newFiles = [...uploadingFiles];
    if (newFiles[index].preview) {
      URL.revokeObjectURL(newFiles[index].preview!);
    }
    newFiles.splice(index, 1);
    setUploadingFiles(newFiles);
  };

  const handleDeleteAttachment = (attachmentId: string, fileName: string) => {
    setAttachmentToDelete({ id: attachmentId, name: fileName });
    setShowDeleteAttachmentConfirm(true);
  };

  const confirmDeleteAttachment = async () => {
    if (!attachmentToDelete) return;
    
    setDeletingAttachment(attachmentToDelete.id);
    try {
      await tasksApi.deleteAttachment(attachmentToDelete.id);
      showSuccess('Attachment Deleted', `${attachmentToDelete.name} has been deleted`);
      await loadTaskDetail({ silent: true });
    } catch (err: any) {
      showError('DELETE_FAILED', err.message || 'Failed to delete attachment');
    } finally {
      setDeletingAttachment(null);
      setShowDeleteAttachmentConfirm(false);
      setAttachmentToDelete(null);
    }
  };

  const cancelDeleteAttachment = () => {
    setShowDeleteAttachmentConfirm(false);
    setAttachmentToDelete(null);
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType?.startsWith('image/')) return '🖼️';
    if (mimeType?.startsWith('video/')) return '🎥';
    if (mimeType?.startsWith('audio/')) return '🎵';
    if (mimeType?.includes('pdf')) return '📄';
    if (mimeType?.includes('word') || mimeType?.includes('document')) return '📝';
    if (mimeType?.includes('excel') || mimeType?.includes('sheet')) return '📊';
    if (mimeType?.includes('powerpoint') || mimeType?.includes('presentation')) return '📽️';
    return '📎';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleStatusChange = async () => {
    if (!newStatus) {
      showError('CANNOT_BLOCK_WITHOUT_REASON', 'Please select a status');
      return;
    }

    if (newStatus === 'BLOCKED' && !blockedReason.trim()) {
      showError('CANNOT_BLOCK_WITHOUT_REASON', 'Please provide a reason for putting this task on hold');
      return;
    }

    await guard(async () => {
      const res = await tasksApi.changeStatus(taskId, {
        status: newStatus,
        blocked_reason: newStatus === 'BLOCKED' ? blockedReason : undefined,
      });
      setShowStatusModal(false);
      setNewStatus('');
      setBlockedReason('');
      showSuccess('Status Updated', `Task status changed to ${newStatus}`);
      applyMutationResponse(res?.data?.task);
    }, 'CANNOT_MODIFY_OTHERS_TASK');
  };

  const handleProgressUpdate = async () => {
    if (progressPercent < 0 || progressPercent > 100) {
      showError('CANNOT_UPDATE_PROGRESS_BEYOND_LIMIT', 'Progress must be between 0 and 100');
      return;
    }

    await guard(async () => {
      const res = await tasksApi.updateProgress(taskId, {
        progress_percent: progressPercent,
        actual_hours: actualHours ? parseFloat(actualHours) : undefined,
        note: progressNote || undefined,
      });
      setShowProgressModal(false);
      setActualHours('');
      setProgressNote('');
      showSuccess('Progress Updated', `Task progress updated to ${progressPercent}%`);
      applyMutationResponse(res?.data?.task);
    }, 'CANNOT_UPDATE_PROGRESS_ON_COMPLETED');
  };

  const handleDelete = async () => {
    await guard(async () => {
      await tasksApi.delete(taskId);
      showSuccess('Task Deleted', 'The task has been successfully deleted');
      setTimeout(() => navigate(tasksHome), 1500);
    }, 'CANNOT_DELETE_OTHERS_TASK');
  };

  // Save inline title edit
  const handleSaveTitle = async () => {
    const next = titleDraft.trim();
    if (!next) {
      showError('TITLE_REQUIRED', 'Task title cannot be empty.');
      return;
    }
    if (!task || next === task.title) {
      setIsEditingTitle(false);
      return;
    }
    setSavingField('title');
    await guard(async () => {
      const res = await tasksApi.update(taskId!, { title: next });
      showSuccess('Task Updated', 'Title saved.');
      applyMutationResponse(res?.data?.task);
      setIsEditingTitle(false);
    }, 'CANNOT_MODIFY_OTHERS_TASK');
    setSavingField(null);
  };

  // Save inline description edit
  const handleSaveDescription = async () => {
    const next = descriptionDraft;
    if (!task || (next || '') === (task.description || '')) {
      setIsEditingDescription(false);
      return;
    }
    setSavingField('description');
    await guard(async () => {
      const res = await tasksApi.update(taskId!, { description: next });
      showSuccess('Task Updated', 'Description saved.');
      applyMutationResponse(res?.data?.task);
      setIsEditingDescription(false);
    }, 'CANNOT_MODIFY_OTHERS_TASK');
    setSavingField(null);
  };

  const startEditTitle = () => {
    if (!task) return;
    setTitleDraft(task.title || '');
    setIsEditingTitle(true);
  };

  const startEditDescription = () => {
    if (!task) return;
    setDescriptionDraft(task.description || '');
    setIsEditingDescription(true);
  };

  // Clear the description (Delete icon on the description card).
  const handleDeleteDescription = () => {
    if (!task || !task.description) return;
    setConfirm({
      title: 'Delete the description?',
      description: 'This cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
      onConfirm: async () => {
        setSavingField('description');
        await guard(async () => {
          const res = await tasksApi.update(taskId!, { description: '' });
          showSuccess('Description Removed', 'Description has been deleted.');
          applyMutationResponse(res?.data?.task);
          setIsEditingDescription(false);
        }, 'CANNOT_MODIFY_OTHERS_TASK');
        setSavingField(null);
      },
    });
  };

  const formatExactDate = (dateStr: string | null) => {
    if (!dateStr) return 'Not set';
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
    const d = new Date(isDateOnly ? `${dateStr}T00:00:00` : dateStr);
    if (isNaN(d.getTime())) return 'Not set';
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Not set';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / 86400000);

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString();
  };

  const priorityColors = {
    URGENT: 'bg-gradient-to-r from-red-500 to-red-600 text-white',
    HIGH: 'bg-gradient-to-r from-orange-500 to-orange-600 text-white',
    MEDIUM: 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white',
    LOW: 'bg-gradient-to-r from-green-500 to-green-600 text-white',
  };

  const priorityIcons = {
    URGENT: '🔴',
    HIGH: '🟠',
    MEDIUM: '🟡',
    LOW: '🟢',
  };

  const isReviewerForTask = (assignees ?? []).some(a => a.user_id === currentUser?.id && a.role_in_task === 'REVIEWER' && a.is_active);
  const canEdit = currentUser?.id === task?.created_by || isReviewerForTask || canManageTasks;
  const availableActions = getAvailableActions();
  const statusStats = getStatusStats();

  const isNotStarted = task?.status === 'TODO';
  const LOCKED_TABS: Array<string> = [];
  useEffect(() => {
    if (isNotStarted && LOCKED_TABS.includes(activeTab as any)) {
      setActiveTab('details');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotStarted, activeTab]);

  // Calculate approval progress
  const approvalProgress = (assignees ?? []).filter(a => a.role_in_task === 'OWNER' && a.progress_percent > 0).length;
  const totalApprovers = (assignees ?? []).filter(a => a.role_in_task === 'OWNER').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading task details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center bg-red-50 border border-red-200 rounded-2xl p-8 max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <p className="text-red-600 font-medium">{error}</p>
          <button
            onClick={loadTaskDetail}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📋</div>
          <p className="text-gray-500">Task not found</p>
          <button
            onClick={() => navigate(tasksHome)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Tasks
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = statusFlow[task.status as keyof typeof statusFlow] || statusFlow['TODO'];

  return (
    <div className="gt-themed gt-task-detail">
      <div className="gt-shell min-h-screen">
        {/* Header Section */}
        <div className="gt-task-detail__header">
          <div className="gt-task-detail__header-inner">
            <button
              onClick={() => navigate(-1)}
              className="gt-task-detail__back"
              aria-label="Back"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Back</span>
            </button>

            <div className="gt-task-detail__actions">
              {/* Quick Action Buttons */}
              {availableActions.map((action) => (
                <button
                  key={action.action}
                  onClick={async () => {
                    if ((action as any).isReopen) {
                      await guard(async () => {
                        const res = await tasksApi.reopen(taskId!);
                        showSuccess('Task Reopened', 'Task moved back to TODO');
                        applyMutationResponse(res?.data?.task);
                      }, 'CANNOT_MODIFY_OTHERS_TASK');
                      return;
                    }
                    action.isSendForReview
                      ? handleSendForReview()
                      : handleQuickStatusChange(action.action, action.requiresConfirm, action.confirmMessage);
                  }}
                  className={`gt-task-detail__action-btn gt-task-detail__action-btn--${action.color}`}
                >
                  {action.label}
                </button>
              ))}

              <button
                onClick={() => setShowStatusModal(true)}
                className="gt-task-detail__action-btn gt-task-detail__action-btn--indigo"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                <span>Change Status</span>
              </button>
              <button
                onClick={() => {
                  if (isNotStarted) {
                    showWarning('Start the task first', 'Click ▶️ Start Working before updating progress.');
                    return;
                  }
                  setShowProgressModal(true);
                }}
                disabled={isNotStarted}
                title={isNotStarted ? 'Start the task first to update progress' : undefined}
                className="gt-task-detail__action-btn gt-task-detail__action-btn--cyan"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                <span>Update Progress</span>
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="gt-task-detail__main">
          {/* Status Progress Bar */}
          <div className="gt-task-detail__progress">
            <div className="gt-task-detail__progress-row">
              <span>Task Progress</span>
              <span>{Math.round(statusStats.progress)}% Complete</span>
            </div>
            <div className="gt-task-detail__progress-bar">
              <div
                className="gt-task-detail__progress-fill"
                style={{ width: `${statusStats.progress}%` }}
              />
            </div>
            <div className="gt-task-detail__progress-row gt-task-detail__progress-row--labels">
              <span>Created</span>
              <span>In Progress</span>
              <span>Review</span>
              <span>Complete</span>
            </div>
          </div>

          {/* Status Journey Strip */}
          <div className="gt-task-detail__journey-card">
            <JourneyStrip
              currentStatus={task.status}
              createdBy={getUserName(task.created_by)}
              assigneeName={getUserName(task.primary_assignee_id)}
            />
          </div>

          {/* Title Section with Priority Strip */}
          <div className="gt-task-detail__title-wrap">
            <div className={`gt-task-detail__priority-strip gt-task-detail__priority-strip--${(task.priority || 'LOW').toLowerCase()}`} />

            <div className="gt-task-detail__title-body">
              <div className="gt-task-detail__title-meta">
                <span className={`gt-task-detail__priority-chip ${priorityColors[task.priority as keyof typeof priorityColors]}`}>
                  {priorityIcons[task.priority as keyof typeof priorityIcons]} {task.priority} Priority
                </span>
                {task.is_overdue && (
                  <span className="gt-task-detail__overdue-chip">
                    ⚠️ Overdue
                  </span>
                )}
                <div className="gt-task-detail__mute">
                  <MuteTaskButton taskId={task.id} />
                </div>
              </div>
              <div className="flex items-start gap-3 flex-wrap">
                {isEditingTitle ? (
                  <div className="flex-1 min-w-[200px] flex items-center gap-2">
                    <input
                      autoFocus
                      type="text"
                      value={titleDraft}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveTitle();
                        if (e.key === 'Escape') setIsEditingTitle(false);
                      }}
                      className="flex-1 text-2xl md:text-3xl font-bold text-gray-900 bg-white border-2 border-blue-400 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Task title"
                    />
                    <button
                      type="button"
                      onClick={handleSaveTitle}
                      disabled={savingField === 'title'}
                      className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
                    >
                      {savingField === 'title' ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsEditingTitle(false)}
                      className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <>
                    <h1 className="gt-task-detail__title-text">{task.title}</h1>
                    {canEdit && (
                      <div className="flex items-center gap-1.5 mt-1">
                        <button
                          type="button"
                          onClick={startEditTitle}
                          aria-label="Edit task title"
                          title="Edit title"
                          className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowDeleteConfirm(true)}
                          aria-label="Delete task"
                          title="Delete task"
                          className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              <div className="flex flex-wrap gap-3 items-center mt-3">
                <div
                  className="flex items-center gap-1.5 px-3 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-700 font-mono select-all"
                  title="Task ID (read-only)"
                >
                  <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  {task.code || task.id}
                </div>
                <div className="flex items-center gap-2 px-3 py-1 rounded-full" style={{ backgroundColor: statusConfig.bg }}>
                  <span>{statusConfig.icon}</span>
                  <span className="text-sm font-medium" style={{ color: statusConfig.color }}>{statusConfig.label}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Created {formatDate(task.created_at)} by {task.created_by_name || getUserName(task.created_by)}</span>
                </div>
                {task.client_name && (
                  <div className="flex items-center gap-1.5 px-3 py-1 bg-indigo-50 rounded-full text-sm text-indigo-700 font-medium">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    {task.client_name}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div style={{ borderBottom: '1px solid var(--gt-border)', marginBottom: 24 }}>
            <nav className="flex gap-1 overflow-x-auto flex-wrap">
              {[
                {
                  id: 'details' as const, label: 'Details', count: null as number | null,
                  icon: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
                },
                {
                  id: 'checklist' as const, label: 'Checklist', count: checklist?.length ?? 0,
                  icon: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>,
                },
                {
                  id: 'comments' as const, label: 'Comments', count: comments?.length ?? 0,
                  icon: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>,
                },
                {
                  id: 'attachments' as const, label: 'Attachments', count: attachments?.length ?? 0,
                  icon: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>,
                },
                {
                  id: 'activity' as const, label: 'Activity', count: activity?.length ?? 0,
                  icon: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
                },
              ].map((tab) => {
                const isActive = activeTab === tab.id;
                const isLocked = isNotStarted && LOCKED_TABS.includes(tab.id as any);
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      if (isLocked) {
                        showWarning('Start the task first', 'Click ▶️ Start Working before opening this tab.');
                        return;
                      }
                      setActiveTab(tab.id);
                    }}
                    disabled={isLocked}
                    title={isLocked ? 'Start the task first to access this tab' : undefined}
                    className="transition-all"
                    style={{
                      padding: '10px 18px',
                      fontFamily: 'var(--gt-font-display)',
                      fontSize: 13,
                      fontWeight: 600,
                      letterSpacing: '0.01em',
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                      border: 'none',
                      borderBottom: isActive ? '2px solid var(--gt-primary)' : '2px solid transparent',
                      background: isActive ? 'var(--gt-surface-0)' : 'transparent',
                      color: isActive ? 'var(--gt-primary-deep)' : 'var(--gt-text-tertiary)',
                      boxShadow: isActive ? 'var(--gt-elev-1)' : 'none',
                      cursor: isLocked ? 'not-allowed' : 'pointer',
                      opacity: isLocked ? 0.45 : 1,
                      whiteSpace: 'nowrap',
                      marginBottom: -1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 22, height: 22, borderRadius: 7,
                      background: isActive ? 'var(--gt-primary-container)' : 'var(--gt-surface-2)',
                      color: isActive ? 'var(--gt-primary-deep)' : 'var(--gt-text-tertiary)',
                      transition: 'all 160ms',
                    }}>
                      {tab.icon}
                    </span>
                    {tab.label}
                    {isLocked && (
                      <span aria-label="locked" title="Start the task first" style={{ fontSize: 11 }}>🔒</span>
                    )}
                    {tab.count !== null && (
                      <span className="gt-numerals" style={{
                        padding: '1px 8px',
                        borderRadius: 999,
                        background: isActive ? 'var(--gt-primary)' : 'var(--gt-surface-2)',
                        color: isActive ? '#ffffff' : 'var(--gt-text-tertiary)',
                        fontSize: 10.5, fontWeight: 700,
                      }}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {isNotStarted && (
            <div
              className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
              role="status"
            >
              <span className="text-xl" aria-hidden>🔒</span>
              <div className="flex-1 min-w-[240px]">
                <p className="text-sm font-semibold text-amber-900">
                  This task hasn't been started yet
                </p>
                <p className="text-xs text-amber-800 mt-0.5">
                  Start the task first to update progress or mark checklist items done.
                </p>
              </div>
              {availableActions.some((a) => a.action === 'IN_PROGRESS') && (
                <button
                  onClick={() => handleQuickStatusChange('IN_PROGRESS', false)}
                  className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-medium hover:from-blue-700 hover:to-blue-800 shadow-sm"
                >
                  ▶️ Start Working
                </button>
              )}
            </div>
          )}

          {(activeTab === 'details' || activeTab === 'attachments') && (
            <>
              {activeTab === 'details' && (<>
              {/* Description */}
              {(task.description || canEdit) && (
                <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 flex-1">Description</h3>
                    {canEdit && !isEditingDescription && (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={startEditDescription}
                          aria-label="Edit description"
                          title={task.description ? 'Edit description' : 'Add description'}
                          className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {task.description && (
                          <button
                            type="button"
                            onClick={handleDeleteDescription}
                            disabled={savingField === 'description'}
                            aria-label="Delete description"
                            title="Delete description"
                            className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {isEditingDescription ? (
                    <div className="pl-0 sm:pl-10 space-y-2">
                      <textarea
                        autoFocus
                        value={descriptionDraft}
                        onChange={(e) => setDescriptionDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') setIsEditingDescription(false);
                        }}
                        rows={4}
                        placeholder="Describe the task…"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
                      />
                      <div className="flex gap-2 justify-end">
                        <button
                          type="button"
                          onClick={() => setIsEditingDescription(false)}
                          className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveDescription}
                          disabled={savingField === 'description'}
                          className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
                        >
                          {savingField === 'description' ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-700 leading-relaxed pl-10 whitespace-pre-wrap">
                      {task.description || <span className="text-gray-400 italic">No description yet — click the pencil to add one.</span>}
                    </p>
                  )}
                </div>
              )}

              {/* Task Information Grid */}
              <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Task Information</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-500">Task Type</span>
                    <span className="text-sm text-gray-900 font-medium">{task.task_type}</span>
                  </div>
                  {task.client_name && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-500">Client</span>
                      <span className="text-sm text-indigo-700 font-medium">{task.client_name}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-500">Progress</span>
                    <div className="flex items-center gap-3">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${displayedProgress(task.progress_percent, task.status)}%` }}
                        />
                      </div>
                      <span className="text-sm font-semibold text-green-600">{displayedProgress(task.progress_percent, task.status)}%</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-500">Estimated Hours</span>
                    <span className="text-sm text-gray-900">{task.estimated_hours || 'Not set'}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-500">Actual Hours</span>
                    <span className="text-sm text-gray-900">{task.actual_hours || 0}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-500">Start Date</span>
                    <span className="text-sm text-gray-900">{formatExactDate(task.start_date)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-500">Due Date</span>
                    <span className={`text-sm font-medium ${task.is_overdue ? 'text-red-600' : 'text-gray-900'}`}>
                      {formatExactDate(task.due_date)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-500">Last Updated</span>
                    <span className="text-sm text-gray-900">{formatDate(task.updated_at)}</span>
                  </div>
                </div>
              </div>

              {/* Tags */}
              {task.tags && task.tags.length > 0 && (
                <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l5 5a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-5-5A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Tags</h3>
                  </div>
                  <div className="flex flex-wrap gap-2 pl-10">
                    {task.tags.map((tag, i) => (
                      <span key={i} className="px-3 py-1 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 text-sm rounded-full shadow-sm">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Approval Chain Section */}
              <ApprovalChain assignees={assignees} />

              {/* Assignees Section */}
              <AssigneeSelector
                taskId={taskId!}
                assignees={assignees}
                onRefresh={() => loadTaskDetail({ silent: true })}
              />
              </>)}

              {/* Attachments tab — same JSX as before, gated on activeTab */}
              {activeTab === 'attachments' && (
              <div
                ref={attachmentDropRef}
                tabIndex={-1}
                className={`bg-white rounded-xl shadow-md p-6 mb-6 outline-none transition-all ${
                  attachmentDragOver ? 'ring-2 ring-blue-400 ring-offset-2 bg-blue-50/40' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-gray-500 to-gray-600 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Attachments</h3>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">{attachments.length}</span>
                  </div>

                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Attachment
                    </button>
                    <p className="text-[11px] text-gray-500 mt-1 text-right">
                      Drag &amp; drop or paste · Max 5 MB per file
                    </p>
                  </div>
                </div>

                {/* Rejected files banner — over the 5 MB limit */}
                {rejectedFiles.length > 0 && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-red-800">
                          {rejectedFiles.length === 1
                            ? '1 file exceeds the 5 MB limit and was not added'
                            : `${rejectedFiles.length} files exceed the 5 MB limit and were not added`}
                        </p>
                        <ul className="mt-1 space-y-0.5">
                          {rejectedFiles.map((f, i) => (
                            <li key={i} className="flex items-center justify-between text-xs text-red-700">
                              <span className="truncate">
                                <span className="font-medium">{f.name}</span>
                                <span className="text-red-500 ml-2">{formatFileSize(f.size)}</span>
                              </span>
                              <button
                                onClick={() => dismissRejection(i)}
                                className="ml-3 p-0.5 text-red-500 hover:text-red-700"
                                aria-label="Dismiss"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Upload Queue */}
                {uploadingFiles.length > 0 && (
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium text-blue-800">Uploading Files ({uploadingFiles.length})</h4>
                      {!uploading && (
                        <button
                          onClick={handleUpload}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Upload All
                        </button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {uploadingFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {file.type?.startsWith('image/') && file.preview ? (
                              <button
                                type="button"
                                onClick={() => setPreviewImage({ src: file.preview!, name: file.name })}
                                className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md"
                                aria-label={`Preview ${file.name}`}
                                title="Click to preview"
                              >
                                <img
                                  src={file.preview}
                                  alt={file.name}
                                  className="w-10 h-10 rounded-md object-cover border border-gray-200 cursor-zoom-in hover:opacity-90 transition-opacity"
                                />
                              </button>
                            ) : (
                              <span className="text-2xl flex-shrink-0">{getFileIcon(file.type)}</span>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
                              <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {file.uploadProgress ? (
                              <div className="w-24">
                                <div className="bg-gray-200 rounded-full h-1.5">
                                  <div 
                                    className="bg-green-500 h-1.5 rounded-full transition-all"
                                    style={{ width: `${file.uploadProgress}%` }}
                                  />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">{file.uploadProgress}%</p>
                              </div>
                            ) : (
                              <button
                                onClick={() => removeQueuedFile(idx)}
                                className="p-1 text-red-500 hover:text-red-700 transition-colors"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Attachments List */}
                {attachments.length === 0 && uploadingFiles.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-16 h-16 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    <p className="text-sm font-medium">No attachments</p>
                    <p className="text-xs mt-1">Drag &amp; drop files, paste with Ctrl/Cmd+V, or click "Add Attachment"</p>
                    <p className="text-xs text-gray-400 mt-2">Supported: Images, PDF, DOC, XLS, TXT (Max 5 MB)</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {attachments.map((att) => (
                      <div
                        key={att.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {/* Real image thumbnail / PDF badge / generic
                              file icon — the thumb itself is a link to
                              the file. Replaces the previous emoji-only
                              icon so uploaded images surface a real
                              preview, matching the staged view in the
                              Task Create / Edit modals. */}
                          <AttachmentThumb
                            url={att.file_url ?? null}
                            mime={att.mime_type ?? null}
                            name={att.file_name}
                          />
                          <a
                            href={att.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 min-w-0"
                          >
                            <p className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                              {att.file_name}
                            </p>
                            <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                              <span>{formatFileSize(att.file_size)}</span>
                              <span>•</span>
                              <span>Uploaded {formatDate(att.created_at)}</span>
                              <span>•</span>
                              <span>by {att.uploaded_by_name || getUserName(att.uploaded_by)}</span>
                            </div>
                          </a>
                        </div>
                        <button
                          onClick={() => handleDeleteAttachment(att.id, att.file_name)}
                          disabled={deletingAttachment === att.id}
                          className="opacity-0 group-hover:opacity-100 p-2 text-red-500 hover:text-red-700 transition-all disabled:opacity-50"
                        >
                          {deletingAttachment === att.id ? (
                            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          )}
                        </button>
                      </div>
                    ))}
                    
                    {uploadingFiles.length > 0 && uploading && (
                      <div className="border-t border-gray-200 pt-3 mt-3">
                        <p className="text-sm text-gray-500 mb-2">Uploading...</p>
                        {uploadingFiles.map((file, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-2">
                            <div className="animate-pulse w-10 h-10 bg-gray-200 rounded-lg"></div>
                            <div className="flex-1">
                              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2 animate-pulse"></div>
                              <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                            </div>
                            <div className="w-24">
                              <div className="bg-gray-200 rounded-full h-1.5">
                                <div 
                                  className="bg-green-500 h-1.5 rounded-full transition-all"
                                  style={{ width: `${file.uploadProgress || 0}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}
            </>
          )}

          {activeTab === 'checklist' && (
            <Checklist
              taskId={taskId!}
              items={checklist}
              onRefresh={() => loadTaskDetail({ silent: true })}
              parentTaskTitle={task?.title}
              lockToggle={isNotStarted}
              lockMessage="Start the task first to mark checklist items done."
            />
          )}

          {activeTab === 'comments' && (
            <CommentSection
              taskId={taskId!}
              comments={comments}
              onRefresh={() => loadTaskDetail({ silent: true })}
            />
          )}

          {/* Attachments tab content is rendered inside the details branch
              via a conditional flag — see "Attachments Section" below. */}

          {activeTab === 'activity' && (
            /* Activity Log Section with Timeline */
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Activity Log</h3>
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">{activity.length}</span>
              </div>

              {activity.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <svg className="w-16 h-16 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm">No activity yet</p>
                  <p className="text-xs mt-1">Activity will appear here as changes are made</p>
                </div>
              ) : (
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                  <div className="space-y-4 max-h-[500px] overflow-y-auto relative">
                    {[...activity]
                      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                      .map((act) => {
                        const actorName = act.actor_name || getUserName(act.actor_user_id);
                        const actionLabel: Record<string, string> = {
                          CREATED: 'created this task',
                          UPDATED: 'updated the task',
                          ASSIGNED: 'assigned a member',
                          UNASSIGNED: 'removed a member',
                          REASSIGNED: 'reassigned the task',
                          STATUS_CHANGED: 'changed the status',
                          PRIORITY_CHANGED: 'changed the priority',
                          DUE_DATE_CHANGED: 'updated the due date',
                          PROGRESS_UPDATED: 'updated progress',
                          COMMENTED: 'left a comment',
                          MENTIONED: 'mentioned someone',
                          ATTACHMENT_ADDED: 'attached a file',
                          ATTACHMENT_REMOVED: 'removed a file',
                          CHECKLIST_ADDED: 'added a checklist item',
                          CHECKLIST_DONE: 'completed a checklist item',
                          CHECKLIST_UNDONE: 'unchecked a checklist item',
                          CHECKLIST_REMOVED: 'removed a checklist item',
                          BLOCKED: 'put the task on hold',
                          UNBLOCKED: 'resumed the task from hold',
                          REOPENED: 'reopened the task',
                          COMPLETED: 'completed the task',
                          DELETED: 'deleted the task',
                        };
                        const label = actionLabel[act.action] ?? act.action.toLowerCase().replace(/_/g, ' ');
                        return (
                          <div key={act.id} className="relative flex gap-3">
                            <div className="relative z-10">
                              <div className="w-8 h-8 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full flex items-center justify-center text-white text-sm font-bold">
                                {actorName.charAt(0).toUpperCase()}
                              </div>
                            </div>
                            <div className="flex-1 bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
                              <div className="flex justify-between items-start flex-wrap gap-2">
                                <div>
                                  <span className="font-medium text-gray-900 text-sm">{actorName}</span>
                                  <span className="text-gray-500 text-sm ml-1">{label}</span>
                                </div>
                                <span className="text-xs text-gray-400 whitespace-nowrap">{formatDate(act.created_at)}</span>
                              </div>
                              {act.message && (
                                <p className="text-sm text-gray-600 mt-1">{act.message}</p>
                              )}
                              {act.from_value && act.to_value && (
                                <div className="mt-2 flex items-center gap-2 text-xs">
                                  <span className="px-2 py-0.5 bg-gray-200 rounded text-gray-600">{act.from_value}</span>
                                  <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                  </svg>
                                  <span className="px-2 py-0.5 bg-green-100 rounded text-green-700">{act.to_value}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Status Change Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowStatusModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Change Status</h3>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Status</label>
                <Select
                  value={newStatus || "__none__"}
                  onValueChange={(v) => setNewStatus(v === "__none__" ? "" : v)}
                >
                  <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Select status</SelectItem>
                    <SelectItem value="TODO">📋 To Do</SelectItem>
                    <SelectItem value="IN_PROGRESS">🔄 In Progress</SelectItem>
                    <SelectItem value="IN_REVIEW">👀 In Review</SelectItem>
                    <SelectItem value="BLOCKED">🚫 Hold</SelectItem>
                    <SelectItem value="DONE">✅ Done</SelectItem>
                    <SelectItem value="CANCELLED">❌ Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newStatus === 'BLOCKED' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hold Reason</label>
                  <textarea
                    placeholder="Explain why this task is blocked..."
                    value={blockedReason}
                    onChange={(e) => setBlockedReason(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                  />
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowStatusModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusChange}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-sm"
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hold Task Modal */}
      {showBlockModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowBlockModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm animate-in fade-in zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-red-600 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </div>
                <h3 className="text-base font-bold text-gray-900">Hold Task</h3>
              </div>
            </div>
            <div className="px-4 py-3 space-y-2">
              <p className="text-sm text-gray-600">
                Please provide a reason for putting this task on hold
                <span className="text-red-600 ml-0.5">*</span>
              </p>
              <textarea
                autoFocus
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="Explain why this task is being put on hold..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
              />
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setShowBlockModal(false)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmHoldTask}
                className="px-3 py-1.5 text-sm bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-sm"
              >
                Hold Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Update Modal */}
      {showProgressModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowProgressModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Update Progress</h3>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Progress (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={progressPercent}
                  onChange={(e) => setProgressPercent(parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Actual Hours (optional)</label>
                <input
                  type="number"
                  step="0.5"
                  value={actualHours}
                  onChange={(e) => setActualHours(e.target.value)}
                  placeholder="Enter actual hours spent"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Note (optional)</label>
                <textarea
                  value={progressNote}
                  onChange={(e) => setProgressNote(e.target.value)}
                  placeholder="Add a note about this progress update..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowProgressModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleProgressUpdate}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-sm"
              >
                Update Progress
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Task Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Delete Task</h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-600">Are you sure you want to delete this task?</p>
              <p className="text-sm text-red-500 mt-2">This action cannot be undone.</p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-sm"
              >
                Delete Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Attachment Confirmation Modal */}
      {showDeleteAttachmentConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={cancelDeleteAttachment}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Delete Attachment</h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-600">Are you sure you want to delete <span className="font-semibold">{attachmentToDelete?.name}</span>?</p>
              <p className="text-sm text-red-500 mt-2">This action cannot be undone.</p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={cancelDeleteAttachment}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteAttachment}
                className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-sm"
              >
                Delete Attachment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image preview lightbox — opened from upload-queue thumbnails */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`Preview of ${previewImage.name}`}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setPreviewImage(null); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            aria-label="Close preview"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div
            className="flex flex-col items-center max-w-[95vw] max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={previewImage.src}
              alt={previewImage.name}
              className="max-w-full max-h-[80vh] rounded-lg shadow-2xl object-contain"
            />
            <p className="mt-3 text-sm text-white/90 truncate max-w-[80vw]">{previewImage.name}</p>
          </div>
        </div>
      )}

      {/* Popup Component for Notifications */}
      <Popup
        isOpen={popup.isOpen}
        type={popup.type}
        title={popup.title}
        message={popup.message}
        onClose={closePopup}
      />

      {/* In-app confirmation dialog (replaces native window.confirm). */}
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />

      {/* Animation Styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes zoomIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-in {
          animation-duration: 0.2s;
          animation-fill-mode: both;
        }
        .fade-in {
          animation-name: fadeIn;
        }
        .zoom-in {
          animation-name: zoomIn;
        }
      `}</style>
    </div>
  );
}

export default TaskDetail;
